import { useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import type { CSSProperties } from "react";

export type MorphState = "fab" | "modal" | "closing";

type Geometry = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const EVENT_TOP_FAB_BOTTOM_OFFSET = 68;

const getFabGeometry = (
  vw: number,
  vh: number,
  isMultiEvent: boolean,
  bottomOffset: number
): Geometry => {
  const FAB_WIDTH_SINGLE = 220;
  const FAB_WIDTH_MULTI = 168;
  const FAB_HEIGHT = 58;
  const FAB_MARGIN = 16;
  const FAB_MARGIN_DESKTOP = 24;
  const margin = vw >= 600 ? FAB_MARGIN_DESKTOP : FAB_MARGIN;
  const width = isMultiEvent ? FAB_WIDTH_MULTI : FAB_WIDTH_SINGLE;
  return {
    top: vh - FAB_HEIGHT - margin - bottomOffset,
    left: vw - width - margin,
    width,
    height: FAB_HEIGHT,
  };
};

const getModalGeometry = (vw: number, vh: number): Geometry => {
  const MODAL_MARGIN = 16;
  const MODAL_MAX_WIDTH = 360;
  const MODAL_TARGET_HEIGHT = 500;
  const width = Math.min(MODAL_MAX_WIDTH, vw - MODAL_MARGIN * 2);
  const height = Math.min(MODAL_TARGET_HEIGHT, vh - MODAL_MARGIN * 2);
  return {
    top: Math.max(MODAL_MARGIN, Math.floor((vh - height) / 2)),
    left: Math.floor((vw - width) / 2),
    width,
    height,
  };
};

/**
 * @description
 * morphState に応じて、スタイルを返す.
 * morphState は、以下の3つのいずれか。
 * - "modal": 画面中央に白いモーダル
 * - "fab": 画面右下に緑のFAB
 * - "closing": 閉じている最中
 *
 * このスタイルが返す top/left/width/height/borderRadius/backgroundColor は
 * CSS の transition プロパティの対象になっているため (TodayEventMorphCard.styles.ts の cardStyle 参照)、
 * morphState が切り替わると、新旧の値の間がブラウザによって自動補間され、
 * モーダル ⇔ FAB のモーフィングアニメーションとして表示される。
 */
const getCardInlineStyle = (
  morphState: MorphState,
  vw: number,
  vh: number,
  isMultiEvent: boolean,
  fabBottomOffset: number
): CSSProperties => {
  const geom =
    morphState === "modal"
      ? getModalGeometry(vw, vh)
      : getFabGeometry(vw, vh, isMultiEvent, fabBottomOffset);
  return {
    top: geom.top,
    left: geom.left,
    width: geom.width,
    height: geom.height,
    borderRadius: morphState === "modal" ? 24 : 29,
    backgroundColor: morphState === "modal" ? "#ffffff" : "#00c2ae",
  };
};

/**
 * @description
 * TodayEventMorphCard のスタイルを計算して返すカスタムフック.
 * 画面サイズの変化に対応するため、windowのresizeイベントをリッスンしている。
 * また、/events/:eventId ピッタリのとき (イベントトップ) はページ下部のボタンを避けるためFABを上にオフセットする。
 * それ以外のパスではオフセット不要。
 * これらの条件を踏まえて、getCardInlineStyle を呼び出してスタイルを計算している。
 */
export const useCardInlineStyle = (
  morphState: MorphState,
  isMultiEvent: boolean
): CSSProperties => {
  const [viewport, setViewport] = useState(() => ({
    vw: typeof window !== "undefined" ? window.innerWidth : 360,
    vh: typeof window !== "undefined" ? window.innerHeight : 640,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ vw: window.innerWidth, vh: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // /events/:eventId ピッタリのとき (イベントトップ) はページ下部のボタンを避けるため
  // FAB を上にオフセットする。それ以外のパスではオフセット不要
  const isEventTopPage = useRouteMatch({
    path: "/events/:eventId",
    exact: true,
  });
  const fabBottomOffset = isEventTopPage ? EVENT_TOP_FAB_BOTTOM_OFFSET : 0;

  return getCardInlineStyle(
    morphState,
    viewport.vw,
    viewport.vh,
    isMultiEvent,
    fabBottomOffset
  );
};
